// UI
import { TypographyVariant } from '@components/typography';

import cx from 'classnames';

export type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'gray'
  | 'black';
export type OutlinedButtonColor = Exclude<ButtonColor, 'secondary'>;
export type ButtonVariant = 'filled' | 'outlined' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

const fontVariants = {
  variantFontSizesAndLeading: {
    title1: 'text-4xl leading-loose',
    title3: 'text-2xl leading-4/3',
    title2: 'text-3xl leading-8/5',
    title4: 'text-xl leading-8/5',
    title5: 'text-lg leading-14/9',
    title6: 'text-base leading-normal',
    subtitle1: 'text-base leading-normal',
    subtitle2: 'text-sm leading-10/7',
    body1: 'text-base leading-normal',
    body2: 'text-sm leading-10/7',
    small: 'text-xs leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'tracking-normal',
    title2: 'tracking-md',
    title3: 'tracking-lg',
    title4: 'tracking-lg',
    title5: 'tracking-wider',
    title6: 'tracking-wider',
    subtitle1: 'tracking-xl2',
    subtitle2: 'tracking-xl2',
    body1: 'tracking-wider',
    body2: 'tracking-wider',
    small: 'tracking-normal',
  },
  variantThickness: {
    bold: 'font-bold',
    title1: 'font-semibold',
    title2: 'font-semibold',
    title3: 'font-semibold',
    title4: 'font-semibold',
    title5: 'font-semibold',
    title6: 'font-semibold',
    subtitle1: 'font-semibold',
    subtitle2: 'font-semibold',
    body1: 'font-normal',
    body2: 'font-normal',
    small: 'font-normal',
  },
};

// TODO: Remove unecessary utils
export const getSizeStyleAsClassName = (
  size: ButtonSize,
  variant: ButtonVariant,
  disabled: boolean
): string => {
  const validPYs: { [key: number]: string } = {
    1: 'py-px ',
    2: 'py-0.5 ',
    3: 'py-[3px] ',
    4: 'py-1 ',
    5: 'py-[5px] ',
    6: 'py-1.5 ',
    7: 'py-[7px] ',
    8: 'py-2 ',
    9: 'py-[9px] ',
    10: 'py-2.5 ',
    11: 'py-[11px] ',
    12: 'py-3 ',
  };
  let typographyVariant = 'body1' as TypographyVariant;
  let paddingTopBottom = 12 as number;

  if (size === 'md') {
    typographyVariant = 'body2';
    paddingTopBottom = 10;
  } else if (size === 'sm') {
    typographyVariant = 'small';
    paddingTopBottom = 6;
  }
  // Reduce padding to account for border width (both filled and outlined use 1.5px border)
  if (variant === 'filled' && !disabled) paddingTopBottom -= 1;
  if (variant === 'outlined' && !disabled) paddingTopBottom -= 1;
  if (disabled) paddingTopBottom -= 1;

  const fontSizeStyles =
    fontVariants.variantFontSizesAndLeading[typographyVariant];
  const letterSpacingStyles =
    fontVariants.variantLetterSpacing[typographyVariant];
  return cx(
    fontSizeStyles,
    letterSpacingStyles,
    validPYs[paddingTopBottom],
    'px-4 '
  );
};

const getFilledStyles = (color: ButtonColor): string => {
  return cx(
    // Shared styles for all filled buttons
    'shadow-sm',
    'hover:shadow-md',
    'active:shadow-sm',
    'border-solid',
    'border-[1.5px]',
    {
      // Default & Secondary: Gold/Sunrise button with primary (teal) border
      'bg-gradient-to-b from-secondary-main to-secondary-dark/90 text-primary-main border-primary-main':
        color === 'default' || color === 'secondary',
      'hover:from-secondary-dark hover:to-secondary-dark':
        color === 'default' || color === 'secondary',
      'active:from-secondary-dark active:to-secondary-dark':
        color === 'default' || color === 'secondary',

      // Primary: Savvy Blue/Teal button with secondary (gold) border and text
      'bg-gradient-to-b from-primary-main to-primary-dark/90 text-secondary-main border-secondary-main':
        color === 'primary',
      'hover:from-primary-dark hover:to-primary-dark': color === 'primary',
      'active:from-primary-dark active:to-primary-dark': color === 'primary',

      // Gray button
      'bg-gradient-to-b from-gray-main to-gray-dark/90 text-white border-gray-dark':
        color === 'gray',
      'hover:from-gray-dark hover:to-gray-dark': color === 'gray',
      'active:from-gray-dark active:to-gray-dark': color === 'gray',

      // Black button (using gray-dark as brand avoids pure black)
      'bg-gradient-to-b from-gray-dark to-black/90 text-white border-black':
        color === 'black',
      'hover:from-black hover:to-black': color === 'black',
      'active:from-black active:to-black': color === 'black',
    }
  );
};

const getOutlinedStyles = (color: ButtonColor): string => {
  return cx('bg-transparent', 'border-solid', 'border-[1.5px]', {
    // Default (same as primary outlined)
    'border-primary-main text-primary-main':
      color === 'default' || color === 'primary',
    'hover:bg-pool hover:shadow-md': color === 'default' || color === 'primary',
    'active:bg-primary-dark active:text-white':
      color === 'default' || color === 'primary',

    // Secondary (Gold outlined)
    'border-secondary-main text-secondary-main': color === 'secondary',
    'hover:bg-secondary-main hover:text-primary-main hover:shadow-md':
      color === 'secondary',
    'active:bg-secondary-dark active:text-primary-main': color === 'secondary',

    // Gray outlined
    'border-gray-main text-gray-main': color === 'gray',
    'hover:bg-gray-main hover:text-white hover:shadow-md': color === 'gray',
    'active:bg-gray-dark active:text-white': color === 'gray',

    // Black outlined (using primary-main as brand avoids pure black)
    'border-primary-dark text-primary-dark': color === 'black',
    'hover:bg-primary-dark hover:text-white hover:shadow-md': color === 'black',
    'active:bg-primary-main active:text-white': color === 'black',
  });
};

const getTextStyles = (color: ButtonColor): string => {
  // Brand guidelines: "text-primary-main hover:text-primary-light"
  return cx('hover:underline', 'underline-offset-2', {
    // Default matches primary text styling
    'text-primary-main hover:text-primary-light active:text-primary-dark':
      color === 'default' || color === 'primary',
    'text-secondary-main hover:text-secondary-light active:text-secondary-dark':
      color === 'secondary',
    'text-gray-main hover:text-gray-light active:text-gray-dark':
      color === 'gray',
    'text-primary-dark hover:text-primary-light active:text-primary-main':
      color === 'black',
  });
};

const getFocusStyles = (color: ButtonColor, variant: ButtonVariant): string => {
  return cx(
    'focus-visible:outline',
    'focus-visible:outline-2',
    'focus-visible:outline-offset-2',
    {
      // Filled buttons: use contrasting outline color
      'focus-visible:outline-secondary-main':
        variant === 'filled' && color === 'primary', // Gold outline on teal button
      'focus-visible:outline-primary-main':
        variant === 'filled' &&
        (color === 'default' ||
          color === 'secondary' ||
          color === 'black' ||
          color === 'gray'), // Teal outline on gold/dark buttons

      // Outlined and text buttons: match the button color (default matches primary)
      'focus-visible:outline-primary-dark':
        variant !== 'filled' && (color === 'default' || color === 'primary'),
      'focus-visible:outline-secondary-dark':
        variant !== 'filled' && color === 'secondary',
      'focus-visible:outline-gray-dark':
        variant !== 'filled' && (color === 'gray' || color === 'black'),
    }
  );
};

export const getVariantStyleAsClassName = (
  color: ButtonColor,
  variant: ButtonVariant
): string => {
  const focusStyles = getFocusStyles(color, variant);

  switch (variant) {
    case 'filled':
      return cx(getFilledStyles(color), focusStyles);

    case 'outlined':
      return cx(getOutlinedStyles(color), focusStyles);

    case 'text':
      return cx(getTextStyles(color), focusStyles);

    default:
      throw new Error('Invalid variant');
  }
};
